import * as D from 'io-ts/lib/Decoder'

import { ArtistDecoder } from './Artist'
import { PassTierDecoder } from './PassTier'
import { enum_, withDefault } from 'decode/util'
import { populatedArray } from './Populated'

export enum VideoOnDemandType {
    HIGHLIGHT = 'highlight',
    FULL_RECORDING = 'full-recording',
}

export enum VideoOnDemandStatus {
    INITIATED = 'INITIATED',
    READY = 'READY',
    CANCELED = 'CANCELED',
}

export enum WidgetType {
    BUY = 'BUY',
    PARAGRAPH = 'PARAGRAPH',
    LINKS = 'LINKS',
}

export enum MuxStreamActiveType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum WidgetLinkType {
    FACEBOOK = 'facebook',
    TWITTER = 'twitter',
    INSTAGRAM = 'instagram',
    YOUTUBE = 'youtube',
    SPOTIFY = 'spotify',
    WEBSITE = 'website',
    LINKEDIN = 'linkedin',
}

export enum CategoryType {
    MUSIC = 'MUSIC',
    BOOK = 'BOOK',
}

export enum ScopeType {
    PRIVATE = 'PRIVATE',
    PUBLIC = 'PUBLIC',
}

const WidgetLinkDecoder = D.intersection(
    D.type({
        type: enum_(
            WidgetLinkType.FACEBOOK,
            WidgetLinkType.TWITTER,
            WidgetLinkType.INSTAGRAM,
            WidgetLinkType.YOUTUBE,
            WidgetLinkType.SPOTIFY,
            WidgetLinkType.WEBSITE,
            WidgetLinkType.LINKEDIN,
        ),
        linkUrl: D.string,
    }),
    D.partial({
        iconUrl: D.string,
        iconName: D.string,
    }),
)

const VideoOnDemandDecoder = D.intersection(
    D.type({
        _id: D.string,
        playbackId: D.string,
        duration: D.string,
        title: D.string,
        thumbnailFrameTime: D.number,
        type: enum_(VideoOnDemandType.FULL_RECORDING, VideoOnDemandType.HIGHLIGHT),
        status: enum_(VideoOnDemandStatus.INITIATED, VideoOnDemandStatus.READY, VideoOnDemandStatus.CANCELED),
    }),
    D.partial({
        previewGifStartTime: D.number,
        previewGifEndTime: D.number,
        paidVideo: D.boolean,
        expiresAt: D.string,
        startTime: D.number,
    }),
)

export const WidgetContentHyperlinkDecoder = D.type({
    text: D.string,
    url: D.string,
})

export const WidgetContentItemDecoder = D.type({
    imgUrl: D.string,
    callToActionText: D.string,
    callToActionUrl: D.string,
})

export const WidgetContentDecoder = D.intersection(
    D.type({
        heading: D.string,
    }),
    D.partial({
        body: D.string,
        subHeading: D.string,
        hyperlink: WidgetContentHyperlinkDecoder,
        items: D.array(WidgetContentItemDecoder),
        links: D.array(WidgetLinkDecoder),
    }),
)

export const WidgetDecoder = D.type({
    type: enum_(WidgetType.BUY, WidgetType.PARAGRAPH, WidgetType.LINKS),
    content: WidgetContentDecoder,
})

const ShowNoteDecoder = D.type({
    _id: D.string,
    body: D.string,
})

const LuaPassTierDecoder = D.type({
    _id: D.string,
    imgUrl: D.string,
    passTiers: D.array(D.string),
})

const MuxStreamObjectDecoder = D.type({
    muxStreamId: D.string,
    muxStreamUrl: D.string,
})

const MuxStreamDecoder = D.intersection(
    D.type({
        primary: MuxStreamObjectDecoder,
        active: enum_(MuxStreamActiveType.PRIMARY, MuxStreamActiveType.SECONDARY),
    }),
    D.partial({
        secondary: MuxStreamObjectDecoder,
    }),
)

const TrackingIdDecoder = D.partial({
    googleTagId: D.string,
    facebookPixelId: D.string,
})

export const ShowDecoder = D.intersection(
    D.type({
        _id: D.string,
        artist: ArtistDecoder,
        category: enum_(CategoryType.BOOK, CategoryType.MUSIC),
        scope: enum_(ScopeType.PRIVATE, ScopeType.PUBLIC),
        posterImg: D.string,
        coverImgUrl: D.string,
        description: D.string,
        channelName: D.string,
        startTime: D.string,
        endTime: D.string,
        path: D.string,
        muxStream: MuxStreamDecoder,
        passTiers: populatedArray(PassTierDecoder),
        title: D.string,
        location: D.string,
        viewerCap: D.nullable(D.number),
        videosOnDemand: populatedArray(VideoOnDemandDecoder),
        notes: withDefault([], populatedArray(ShowNoteDecoder)),
        luaPassTiers: withDefault([], populatedArray(LuaPassTierDecoder)),
        widgets: withDefault([], populatedArray(WidgetDecoder)),
        disablePaywall: withDefault(false, D.boolean),
        enabledAta: D.boolean,
        hypeVideoVOD: D.nullable(VideoOnDemandDecoder),
        displayFlagLUA: D.boolean,
    }),
    D.partial({
        seriesId: D.string,
        alwaysPlay: D.boolean,
        isPublished: D.boolean,
        willBeRecorded: D.boolean,
        hasExplicitContent: D.boolean,
        featured: D.boolean,
        trackingIds: TrackingIdDecoder,
    }),
)

export interface Show extends D.TypeOf<typeof ShowDecoder> {}
export interface ShowNote extends D.TypeOf<typeof ShowNoteDecoder> {}
export interface LuaPassTier extends D.TypeOf<typeof LuaPassTierDecoder> {}
export interface VideoOnDemand extends D.TypeOf<typeof VideoOnDemandDecoder> {}
export interface Widget extends D.TypeOf<typeof WidgetDecoder> {}
export interface WidgetContent extends D.TypeOf<typeof WidgetContentDecoder> {}
export interface WidgetContentHyperlink extends D.TypeOf<typeof WidgetContentHyperlinkDecoder> {}
export interface WidgetContentItem extends D.TypeOf<typeof WidgetContentItemDecoder> {}
